/**
 * services/queue/queueService.ts
 * The SK Queue API client — real axios calls against sk-queue-api (CC-001 §7 /
 * reconciled to the live route table). Every function maps 1:1 to an endpoint
 * and uses the wire DTOs in api/queueDtos.ts.
 *
 * Auth: requests go through the shared axios instance with credentials; the
 * platform issues an RS256 JWT (aud=queue) carrying the scopes the backend gates
 * on (GUEST_JOIN / QUEUE_OPERATE / QUEUE_VIEW / CONFIG_WRITE / OPERATOR_WRITE).
 * Tenant is resolved server-side from the token (requireTenant).
 *
 * NOTE: the app still runs fully standalone on the in-memory store; this layer
 * is consumed only when the live data source is active (see queue/dataSource).
 */

import type { AxiosRequestConfig } from 'axios';
import axiosInstance from '../../api/axiosInstance';
import {
  Queue_Entries_Url, Entry_Url, Entry_Verify_Url, Entry_Seat_Url, Entry_Release_Url,
  Entry_Remove_Url, Entry_Acknowledge_Url, Queue_Allocate_Url, Queue_Sweep_Url, Queue_Url, Queue_List_Url,
  Queue_Live_Url, Queue_Activity_Url, Queue_Floor_Url, Queue_NowServing_Url, Queue_VoiceMonitor_Url, Queue_ChatMonitor_Url,
  Entitlements_Url, Queue_Resources_Url, Queue_Resource_Url,
  Dashboard_Url, Queue_Overview_Url, Catalog_Products_Url,
} from '../../api/queueEndpoints';
import type {
  JoinRequest, SeatRequest, RemoveRequest, ReleaseRequest, VerifyRequest,
  EntryEnvelope, EntryWithEvents, LiveView, FloorView, NowServingView, VoiceMonitorView,
  ChatMonitorView, ActivityView, QueueListEnvelope, QueueEnvelope, QueuePatchTier1, QueuePatchTier2,
  EntitlementDto, EntitlementsEnvelope, DashboardView, OverviewView, CreateQueueRequest,
  CatalogProduct, CatalogProductsEnvelope,
} from '../../api/queueDtos';

// ── Queue list (active-queue picker) ─────────────────────────────────────────
/** List the org's queues (tenant resolved server-side). Used to pick an active queue. */
export async function listQueues(): Promise<QueueListEnvelope> {
  const { data } = await axiosInstance.get<QueueListEnvelope>(Queue_List_Url);
  return data;
}

/** Read one queue (full config) — backs the Operator (Tier-2) + Config (Tier-1) screens. */
export async function getQueue(queueId: string): Promise<QueueEnvelope> {
  const { data } = await axiosInstance.get<QueueEnvelope>(Queue_Url(queueId));
  return data;
}

// ── Intake / lifecycle ───────────────────────────────────────────────────────
// The public guest surface has no cookie session, so these accept an optional
// axios config to carry the Backbone-minted guest bearer (see guestSession).
export async function joinQueue(queueId: string, body: JoinRequest, cfg?: AxiosRequestConfig): Promise<EntryEnvelope> {
  const { data } = await axiosInstance.post<EntryEnvelope>(Queue_Entries_Url(queueId), body, cfg);
  return data;
}

export async function verifyEntry(entryId: string, body: VerifyRequest, cfg?: AxiosRequestConfig): Promise<EntryEnvelope> {
  const { data } = await axiosInstance.post<EntryEnvelope>(Entry_Verify_Url(entryId), body, cfg);
  return data;
}

/** Guest "on my way" — CALLED → ACKNOWLEDGED, extends the Hold timer (rev 5). */
export async function acknowledgeEntry(entryId: string, cfg?: AxiosRequestConfig): Promise<EntryEnvelope> {
  const { data } = await axiosInstance.post<EntryEnvelope>(Entry_Acknowledge_Url(entryId), {}, cfg);
  return data;
}

// Uses /seat (back-compat alias of the rev-5 /serve verb) so this keeps working
// against both old and new backends during the migration.
export async function seatEntry(entryId: string, body: SeatRequest): Promise<EntryEnvelope> {
  const { data } = await axiosInstance.post<EntryEnvelope>(Entry_Seat_Url(entryId), body);
  return data;
}

export async function releaseEntry(entryId: string, body: ReleaseRequest = {}): Promise<EntryEnvelope> {
  const { data } = await axiosInstance.post<EntryEnvelope>(Entry_Release_Url(entryId), body);
  return data;
}

export async function removeEntry(entryId: string, body: RemoveRequest = {}, cfg?: AxiosRequestConfig): Promise<EntryEnvelope> {
  const { data } = await axiosInstance.post<EntryEnvelope>(Entry_Remove_Url(entryId), body, cfg);
  return data;
}

export async function getEntry(entryId: string, cfg?: AxiosRequestConfig): Promise<EntryWithEvents> {
  const { data } = await axiosInstance.get<EntryWithEvents>(Entry_Url(entryId), cfg);
  return data;
}

// ── Operate ──────────────────────────────────────────────────────────────────
/** Manual "call next" — allocate the front of the FIFO line to a free resource. */
export async function allocateNext(queueId: string): Promise<void> {
  await axiosInstance.post(Queue_Allocate_Url(queueId));
}

/** Run no-show + expiry sweeps now. */
export async function sweepQueue(queueId: string): Promise<void> {
  await axiosInstance.post(Queue_Sweep_Url(queueId));
}

// ── Config ───────────────────────────────────────────────────────────────────
/** PATCH Tier-2 operator toggles (requires OPERATOR_WRITE scope). */
export async function patchOperator(queueId: string, fields: QueuePatchTier2): Promise<void> {
  await axiosInstance.patch(Queue_Url(queueId), fields);
}

/** PATCH Tier-1 config (requires CONFIG_WRITE / Softknack scope). */
export async function patchConfig(queueId: string, fields: QueuePatchTier1): Promise<void> {
  await axiosInstance.patch(Queue_Url(queueId), fields);
}

// ── Request-a-queue (Setup; CONFIG_WRITE) ────────────────────────────────────
/**
 * Provision a queue for the current org (rev 5 request-a-queue). Beyond
 * name/slug/product/match it carries the engine contract: requesting product,
 * feature, output consumer, display vocab. Returns the full new queue.
 */
export async function createQueue(body: CreateQueueRequest): Promise<QueueEnvelope> {
  const { data } = await axiosInstance.post<QueueEnvelope>(Queue_List_Url, body);
  return data;
}

// ── Resources (FITTED tables) ────────────────────────────────────────────────
/** Create a table for a queue (requires OPERATOR_WRITE). */
export async function createResource(
  queueId: string,
  body: { label: string; capacity: number; zone?: string; shared?: boolean },
): Promise<void> {
  await axiosInstance.post(Queue_Resources_Url(queueId), body);
}

/** Delete (archive) a table (requires OPERATOR_WRITE). */
export async function deleteResource(queueId: string, resourceRef: string): Promise<void> {
  await axiosInstance.delete(Queue_Resource_Url(queueId, resourceRef));
}

// ── Entitlements (READ-ONLY) ─────────────────────────────────────────────────
// Entitlements are owned by the platform's subscription system (granted
// automatically on purchase). SK Queue only reads them; there is no write path
// here by design — provisioning is NOT a per-org manual toggle.
/** GET the current tenant's product entitlements (requires QUEUE_VIEW). */
export async function getEntitlements(): Promise<EntitlementDto[]> {
  const { data } = await axiosInstance.get<EntitlementsEnvelope>(Entitlements_Url);
  return data.entitlements;
}

/** GET the tenant's SK Platform product catalog (request-a-queue dropdowns). */
export async function getCatalogProducts(): Promise<CatalogProduct[]> {
  const { data } = await axiosInstance.get<CatalogProductsEnvelope>(Catalog_Products_Url);
  return data.products;
}

// ── Engine surfaces (rev 5) ──────────────────────────────────────────────────
/** Engine home rollup — cross-queue KPIs + per-queue registry rows. */
export async function getDashboard(): Promise<DashboardView> {
  const { data } = await axiosInstance.get<DashboardView>(Dashboard_Url);
  return data;
}
/** Queue Detail → Overview — Signals + line + resource pool + ledger. */
export async function getOverview(queueId: string): Promise<OverviewView> {
  const { data } = await axiosInstance.get<OverviewView>(Queue_Overview_Url(queueId));
  return data;
}

// ── Read models ──────────────────────────────────────────────────────────────
export async function getLive(queueId: string): Promise<LiveView> {
  const { data } = await axiosInstance.get<LiveView>(Queue_Live_Url(queueId));
  return data;
}
/** Append-only activity ledger across the queue's entries (newest first). */
export async function getActivity(queueId: string): Promise<ActivityView> {
  const { data } = await axiosInstance.get<ActivityView>(Queue_Activity_Url(queueId));
  return data;
}
/** Live floor / table grid (FITTED) — backs the Host Console. */
export async function getFloor(queueId: string): Promise<FloorView> {
  const { data } = await axiosInstance.get<FloorView>(Queue_Floor_Url(queueId));
  return data;
}
export async function getNowServing(queueId: string, cfg?: AxiosRequestConfig): Promise<NowServingView> {
  const { data } = await axiosInstance.get<NowServingView>(Queue_NowServing_Url(queueId), cfg);
  return data;
}
export async function getVoiceMonitor(queueId: string): Promise<VoiceMonitorView> {
  const { data } = await axiosInstance.get<VoiceMonitorView>(Queue_VoiceMonitor_Url(queueId));
  return data;
}
export async function getChatMonitor(queueId: string): Promise<ChatMonitorView> {
  const { data } = await axiosInstance.get<ChatMonitorView>(Queue_ChatMonitor_Url(queueId));
  return data;
}
